import { DragDropContext, type OnDragEndResponder } from "@hello-pangea/dnd";
import isEqual from "lodash/isEqual";
import { useDataProvider, useListContext } from "ra-core";
import { useEffect, useState } from "react";

import type { Prospect } from "../types";
import { ProspectColumn } from "./ProspectColumn";
import { prospectStages } from "./prospectModel";

type ProspectsByStage = Record<string, Prospect[]>;

const emptyByStage = (): ProspectsByStage =>
  prospectStages.reduce((acc, s) => ({ ...acc, [s.id]: [] }), {});

const byAuctionDate = (a: Prospect, b: Prospect) => {
  // ascending auction_date, nulls last; tie-break on created_at
  if (a.auction_date && b.auction_date)
    return a.auction_date.localeCompare(b.auction_date);
  if (a.auction_date) return -1;
  if (b.auction_date) return 1;
  return (a.created_at ?? "").localeCompare(b.created_at ?? "");
};

const groupByStage = (prospects: Prospect[]): ProspectsByStage => {
  const grouped = emptyByStage();
  for (const prospect of prospects) {
    const key = grouped[prospect.stage] ? prospect.stage : "Researching";
    grouped[key].push(prospect);
  }
  for (const key of Object.keys(grouped)) grouped[key].sort(byAuctionDate);
  return grouped;
};

export const ProspectPipeline = () => {
  const { data, isPending, refetch } = useListContext<Prospect>();
  const dataProvider = useDataProvider();
  const [byStage, setByStage] = useState<ProspectsByStage>(emptyByStage());

  useEffect(() => {
    if (!data) return;
    const next = groupByStage(data);
    setByStage((prev) => (isEqual(prev, next) ? prev : next));
  }, [data]);

  if (isPending) return null;

  const onDragEnd: OnDragEndResponder = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return; // no intra-column reorder

    const sourceStage = source.droppableId;
    const destStage = destination.droppableId;
    const moved = byStage[sourceStage]?.[source.index];
    if (!moved) return;

    // optimistic local update
    setByStage((prev) => {
      const sourceCol = [...prev[sourceStage]];
      const destCol = [...prev[destStage]];
      sourceCol.splice(source.index, 1);
      destCol.splice(destination.index, 0, { ...moved, stage: destStage });
      return { ...prev, [sourceStage]: sourceCol, [destStage]: destCol };
    });

    // persist
    dataProvider
      .update("prospects", {
        id: draggableId,
        data: { stage: destStage },
        previousData: moved,
      })
      .then(
        () => {
          void refetch();
        },
        () => {
          void refetch();
        },
      );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto">
        {prospectStages.map((stage) => (
          <ProspectColumn
            key={stage.id}
            stage={stage.id}
            label={stage.name}
            prospects={byStage[stage.id] ?? []}
          />
        ))}
      </div>
    </DragDropContext>
  );
};
